import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  QrCode,
  MessageCircle,
  BarChart3,
  Zap,
  Smartphone,
  TrendingUp,
  CheckCircle2,
  ArrowRight,
  X,
  ZoomIn,
  ZoomOut
} from 'lucide-react'
import { useTranslation } from '../core/hooks/useTranslation'
import Footer from '../components/layout/Footer'
import Header from '../components/layout/Header'

export default function HomePage() {
  const { isRTL, t } = useTranslation()
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)

  const openLightbox = (imageSrc: string) => {
    setSelectedImage(imageSrc)
    setZoom(1)
    document.body.style.overflow = 'hidden'
  }

  const closeLightbox = () => {
    setSelectedImage(null)
    setZoom(1)
    document.body.style.overflow = 'unset'
  }

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3))
  }

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.5))
  }

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedImage) {
        closeLightbox()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [selectedImage])

  const features = [
    {
      icon: QrCode,
      title: t('homepageFeatureQRCode'),
      description: t('homepageFeatureQRCodeDesc'),
      color: 'bg-primary-500'
    },
    {
      icon: MessageCircle,
      title: t('homepageFeatureWhatsApp'),
      description: t('homepageFeatureWhatsAppDesc'),
      color: 'bg-green-500',
      isCustomIcon: true,
      customIcon: 'whatsapp'
    },
    {
      icon: BarChart3,
      title: t('homepageFeatureAnalytics'),
      description: t('homepageFeatureAnalyticsDesc'),
      color: 'bg-purple-500'
    },
    {
      icon: Zap,
      title: t('homepageFeatureQuick'),
      description: t('homepageFeatureQuickDesc'),
      color: 'bg-yellow-500'
    },
    {
      icon: Smartphone,
      title: t('homepageFeatureResponsive'),
      description: t('homepageFeatureResponsiveDesc'),
      color: 'bg-slate-600'
    },
    {
      icon: TrendingUp,
      title: t('homepageFeatureSales'),
      description: t('homepageFeatureSalesDesc'),
      color: 'bg-emerald-500'
    }
  ]

  const benefits = [
    t('homepageBenefit1'),
    t('homepageBenefit2'),
    t('homepageBenefit3'),
    t('homepageBenefit4'),
    t('homepageBenefit5'),
    t('homepageBenefit6')
  ]

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Bar */}
      <Header showSignInButton showRegisterButton />

      {/* Hero Section - Split Layout with Visual Effects */}
      <section className="relative pt-12 pb-14 lg:pt-14 lg:pb-18 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 -left-4 w-72 h-72 bg-primary-100 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
          <div className="absolute top-0 -right-4 w-72 h-72 bg-[#1E293B]/10 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob" style={{ animationDelay: '2s' }}></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-primary-50 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob" style={{ animationDelay: '4s' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Side - Content */}
            <div className={`space-y-8 text-center ${isRTL ? 'sm:text-right' : 'sm:text-left'}`}>

              {/* Main Heading */}
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 leading-tight">
                {isRTL ? (
                  <>
                    <span className="block">{t('homepageTitle')}</span>
                    <span className="block text-primary-600 mt-2">{t('homepageTitleSecondary')}</span>
                  </>
                ) : (
                  <>
                    <span className="block">{t('homepageTitle')}</span>
                    <span className="block text-[#1E293B] mt-2">{t('homepageTitleSecondary')}</span>
                  </>
                )}
          </h1>

              {/* Description */}
              <p className={`text-lg text-gray-600 leading-relaxed max-w-lg mx-auto ${isRTL ? 'sm:mr-0 sm:ml-auto' : 'sm:ml-0 sm:mr-auto'}`}>
                {t('homepageDescription')}
              </p>

              {/* CTA Buttons */}
              <div className={`flex flex-col sm:flex-row gap-3 items-center ${isRTL ? 'sm:mr-0 sm:ml-auto' : 'sm:ml-0 sm:mr-auto'}`}>
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all shadow-md hover:shadow-lg hover:scale-105 group"
                >
                  {t('homepageStartFree')}
                  <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''} transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1`} />
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all shadow-sm hover:shadow-md"
                >
                  {t('homepageSignIn')}
                </Link>
              </div>

              {/* Stats - Simple Clean Design */}
              <div className="flex flex-wrap items-center gap-x-8 gap-y-4 pt-6">
                <div className="flex flex-col">
                  <div className="text-3xl font-bold text-[#1E293B]">100%</div>
                  <div className="text-sm text-gray-500 mt-1">{t('homepageStatsFree')}</div>
                </div>
                <div className="w-px h-8 bg-gray-300"></div>
                <div className="flex flex-col">
                  <div className="text-3xl font-bold text-[#1E293B]">5</div>
                  <div className="text-sm text-gray-500 mt-1">{t('homepageStatsMin')}</div>
                </div>
                <div className="w-px h-8 bg-gray-300"></div>
                <div className="flex flex-col">
                  <div className="text-3xl font-bold text-[#1E293B]">24/7</div>
                  <div className="text-sm text-gray-500 mt-1">{t('homepageStatsSupport')}</div>
                </div>
              </div>
            </div>

            {/* Right Side - Image with Cards */}
            <div className="relative hidden lg:block">
              {/* Main Image */}
              <div className="relative rounded-2xl overflow-hidden shadow-2xl cursor-pointer group" onClick={() => openLightbox('/assets/images/landing/task_01k8vng1greqcrs52j5rfycqme_1761865224_img_0.webp')}>
                <img
                  src="/assets/images/landing/task_01k8vng1greqcrs52j5rfycqme_1761865224_img_0.webp"
                  alt={`${t('siteName')} - ${t('siteDescription')}`}
                  className="w-full h-[500px] object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </div>

              {/* Floating Card 1 - Top Left */}
              <div className="absolute -top-4 -left-4 rtl:left-auto rtl:-right-4 animate-float">
                <div className="bg-white rounded-xl p-3 shadow-xl border border-gray-100 cursor-pointer hover:shadow-2xl transition-all hover:scale-110" onClick={() => openLightbox('/assets/images/landing/task_01k8vn8b3cffm8cgehqqngt6k6_1761864932_img_0.webp')}>
                  <img
                    src="/assets/images/landing/task_01k8vn8b3cffm8cgehqqngt6k6_1761864932_img_0.webp"
                    alt={t('homepageQRCard')}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                </div>
              </div>

              {/* Floating Card 2 - Bottom Right */}
              <div className="absolute -bottom-4 -right-4 rtl:right-auto rtl:-left-4 animate-float-delayed">
                <div className="bg-white rounded-xl p-3 shadow-xl border border-gray-100 cursor-pointer hover:shadow-2xl transition-all hover:scale-110" onClick={() => openLightbox('/assets/images/landing/task_01k8vn8b3cffm8cgehqqngt6k6_1761864932_img_1.webp')}>
                  <img
                    src="/assets/images/landing/task_01k8vn8b3cffm8cgehqqngt6k6_1761864932_img_1.webp"
                    alt={t('homepageDigitalMenu')}
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section - Clean Grid */}
      <section className="py-14 lg:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1E293B] mb-3">
              {t('homepageFeaturesTitle')}
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              {t('homepageFeaturesDescription')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all"
                >
                  <div className={`w-12 h-12 rounded-lg ${feature.color} flex items-center justify-center mb-4`}>
                    {feature.isCustomIcon && feature.customIcon === 'whatsapp' ? (
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                      </svg>
                    ) : (
                      <Icon className="w-6 h-6 text-white" />
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section - Split with Image */}
      <section className="py-14 lg:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                {t('homepageBenefitsTitle')}
              </h2>
              <p className="text-base text-gray-600 mb-6 leading-relaxed">
                {t('homepageBenefitsDescription')}
              </p>

              <div className="space-y-3 mb-8">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#1E293B] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>

              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all shadow-md hover:shadow-lg group"
              >
                {t('homepageStartFreeNow')}
                <ArrowRight className={`w-4 h-4 ${isRTL ? 'mr-2 rotate-180' : 'ml-2'} transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1`} />
              </Link>
            </div>

            <div className="relative">
              <div className="rounded-xl overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition-shadow" onClick={() => openLightbox('/assets/images/landing/task_01k8vng1greqcrs52j5rfycqme_1761865224_img_1.webp')}>
                <img
                  src="/assets/images/landing/task_01k8vng1greqcrs52j5rfycqme_1761865224_img_1.webp"
                  alt={t('homepageWhyHatli')}
                  className="w-full h-[400px] object-cover hover:opacity-90 transition-opacity"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section - Timeline Design */}
      <section className="py-14 lg:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1E293B] mb-3">
              {t('homepageHowToStartTitle')}
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              {t('homepageHowToStartDescription')}
            </p>
          </div>

          <div className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-24 left-0 right-0 h-0.5 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200"></div>

            <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
              {/* Step 1 */}
              <div className="relative">
                <div className="bg-white rounded-2xl border-2 border-gray-100 p-6 hover:border-primary-200 hover:shadow-lg transition-all">
                  <div className="relative mb-6">
                    <div className="w-full aspect-square max-w-[200px] mx-auto rounded-xl overflow-hidden shadow-md cursor-pointer hover:shadow-lg transition-shadow" onClick={() => openLightbox('/assets/images/landing/task_01k8vn8b3cffm8cgehqqngt6k6_1761864932_img_0.webp')}>
                      <img
                        src="/assets/images/landing/task_01k8vn8b3cffm8cgehqqngt6k6_1761864932_img_0.webp"
                        alt="Step 1"
                        className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                      />
                    </div>
                    <div className="absolute -top-3 -right-3 w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold shadow-lg border-4 border-white">
                      1
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
                    {t('homepageStep1Title')}
                  </h3>
                  <p className="text-sm text-gray-600 text-center leading-relaxed">
                    {t('homepageStep1Description')}
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative">
                <div className="bg-white rounded-2xl border-2 border-gray-100 p-6 hover:border-primary-200 hover:shadow-lg transition-all">
                  <div className="relative mb-6">
                    <div className="w-full aspect-square max-w-[200px] mx-auto rounded-xl overflow-hidden shadow-md cursor-pointer hover:shadow-lg transition-shadow" onClick={() => openLightbox('/assets/images/landing/task_01k8vn8b3cffm8cgehqqngt6k6_1761864932_img_1.webp')}>
                      <img
                        src="/assets/images/landing/task_01k8vn8b3cffm8cgehqqngt6k6_1761864932_img_1.webp"
                        alt="Step 2"
                        className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                      />
                    </div>
                    <div className="absolute -top-3 -right-3 w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold shadow-lg border-4 border-white">
                      2
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
                    {t('homepageStep2Title')}
                  </h3>
                  <p className="text-sm text-gray-600 text-center leading-relaxed">
                    {t('homepageStep2Description')}
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="relative">
                <div className="bg-white rounded-2xl border-2 border-gray-100 p-6 hover:border-primary-200 hover:shadow-lg transition-all">
                  <div className="relative mb-6">
                    <div className="w-full aspect-square max-w-[200px] mx-auto rounded-xl overflow-hidden shadow-md cursor-pointer hover:shadow-lg transition-shadow" onClick={() => openLightbox('/assets/images/landing/task_01k8vqpcm6em48k1xmj1p007ep_1761867505_img_0.webp')}>
                      <img
                        src="/assets/images/landing/task_01k8vqpcm6em48k1xmj1p007ep_1761867505_img_0.webp"
                        alt="Step 3"
                        className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                      />
                    </div>
                    <div className="absolute -top-3 -right-3 w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-bold shadow-lg border-4 border-white">
                      3
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">
                    {t('homepageStep3Title')}
                  </h3>
                  <p className="text-sm text-gray-600 text-center leading-relaxed">
                    {t('homepageStep3Description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Parallax with Fixed Background */}
      <section
        className="relative py-12 lg:py-16 min-h-[350px] flex items-center"
        style={{
          backgroundImage: `url('/assets/images/background_overlay.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          backgroundRepeat: 'no-repeat',
        }}
      >
        {/* Backdrop Blur Overlay - Lighter */}
        <div className="absolute inset-0 bg-[#1E293B]/85 backdrop-blur-sm"></div>

        {/* Content */}
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 drop-shadow-[0_4px_12px_rgba(0,0,0,0.5)]">
            {t('homepageCTATitle')}
          </h2>

          <p className="text-base sm:text-lg text-white mb-6 max-w-2xl mx-auto drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)] leading-relaxed">
            {t('homepageCTADescription')}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl hover:from-primary-700 hover:to-primary-800 transition-all shadow-xl hover:shadow-primary-500/50 hover:scale-105 group"
            >
              {t('homepageCTACreateAccount')}
              <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''} transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1`} />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center px-6 py-3 text-sm font-semibold text-white bg-white/10 backdrop-blur-lg border-2 border-white/40 rounded-xl hover:bg-white/20 transition-all shadow-xl hover:shadow-white/20 hover:scale-105"
            >
              {t('homepageSignIn')}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer variant="default" />

      {/* Image Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-[100] bg-black bg-opacity-90 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <div className="relative max-w-7xl w-full h-full flex items-center justify-center">
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-10 text-white hover:text-gray-300 transition-colors p-2 bg-black/50 rounded-full hover:bg-black/70"
              aria-label={t('homepageClose')}
            >
              <X className="w-6 h-6" />
            </button>

            <div className="absolute top-4 left-4 z-10 flex gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleZoomIn()
                }}
                className="text-white hover:text-gray-300 transition-colors p-2 bg-black/50 rounded-full hover:bg-black/70"
                aria-label={t('homepageZoomIn')}
              >
                <ZoomIn className="w-6 h-6" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleZoomOut()
                }}
                className="text-white hover:text-gray-300 transition-colors p-2 bg-black/50 rounded-full hover:bg-black/70"
                aria-label={t('homepageZoomOut')}
              >
                <ZoomOut className="w-6 h-6" />
              </button>
          </div>

            <div
              className="w-full h-full flex items-center justify-center cursor-zoom-in"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selectedImage}
                alt={t('homepageZoomedImage')}
                className="max-w-full max-h-full object-contain transition-transform duration-300"
                style={{ transform: `scale(${zoom})` }}
              />
            </div>

            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black/50 px-4 py-2 rounded-full">
              {t('homepagePressEscToClose')}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
